
const NS = 'cc'; // namespace
const CACHE_VERSION = 2; // Version du cache

function k(path) {
  return `${NS}:v${CACHE_VERSION}:${path}`;
}

/**
 * Calcule un hash simple d'un objet pour validation
 * @param {any} obj - Objet à hasher
 * @returns {string} Hash en base 36
 */
function simpleHash(obj) {
  const str = JSON.stringify(obj);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString(36);
}

function isQuotaExceeded(err) {
  if (!err) return false;
  return (
    err.name === 'QuotaExceededError' ||
    err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    err.code === 22 || err.code === 1014
  );
}

// Trim simple : on supprime les plus anciens 'cc:*' jusqu'à réussir 1 set
function trimOldestKeys(limit = 3) {
  try {
    const entries = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${NS}:`)) {
        try {
          const { updatedAt } = JSON.parse(localStorage.getItem(key)) || {};
          entries.push([key, Number(updatedAt) || 0]);
        } catch { /* ignore corrupted here, will be purged later */ }
      }
    }
    entries.sort((a, b) => a[1] - b[1]); // oldest first
    for (let i = 0; i < Math.min(limit, entries.length); i++) {
      localStorage.removeItem(entries[i][0]);
    }
  } catch (e) {
    console.warn('[cache] trimOldestKeys failed:', e?.message || e);
  }
}

export function cacheSet(path, value) {
  const key = k(path);
  const payload = JSON.stringify({ 
    value, 
    version: CACHE_VERSION,
    checksum: simpleHash(value),
    updatedAt: Date.now() 
  });
  try {
    localStorage.setItem(key, payload);
  } catch (err) {
    if (isQuotaExceeded(err)) {
      console.warn('[cacheSet] quota exceeded; trimming…', { key });
      trimOldestKeys(5);
      try {
        localStorage.setItem(key, payload);
      } catch (e2) {
        console.error('[cacheSet] failed after trim:', { key }, e2);
      }
    } else {
      console.warn('[cacheSet] set failed:', { key }, err);
    }
  }
}

export function cacheGet(path, fallback = null) {
  const key = k(path);
  const raw = localStorage.getItem(key);
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw);
    
    // Validation de version
    if (parsed.version && parsed.version !== CACHE_VERSION) {
      console.warn('[cacheGet] version mismatch, invalidating:', { key, expected: CACHE_VERSION, got: parsed.version });
      localStorage.removeItem(key);
      return fallback;
    }
    
    // Validation checksum (si présent)
    if (parsed.checksum && parsed.value) {
      const computedHash = simpleHash(parsed.value);
      if (computedHash !== parsed.checksum) {
        console.warn('[cacheGet] checksum mismatch, invalidating:', { key });
        localStorage.removeItem(key);
        return fallback;
      }
    }
    
    return (parsed && 'value' in parsed) ? parsed.value : fallback;
  } catch (e) {
    console.warn('[cacheGet] corrupted JSON purged:', { key }, e);
    localStorage.removeItem(key);
    return fallback;
  }
}

export function cacheUpdatedAt(path, fallbackTs = 0) {
  const key = k(path);
  const raw = localStorage.getItem(key);
  if (!raw) return fallbackTs;
  try {
    const parsed = JSON.parse(raw);
    return Number(parsed?.updatedAt) || fallbackTs;
  } catch {
    return fallbackTs;
  }
}

// Emet un event quand on met à jour des blocs clés (pour rafraîchir l'UI)
// Utilise BroadcastChannel pour sync multi-onglets
export function emitCacheUpdated(topic) {
  // CORRECTION CRITIQUE: Toujours convertir en string
  const topicStr = Array.isArray(topic) ? topic[0] : String(topic);
  console.log('📡 [emitCacheUpdated] Émission événement pour topic:', topicStr, '(original:', topic, ')');
  
  // Import dynamique pour éviter circular dependency
  import('./broadcast.js').then(({ broadcastCacheUpdate }) => {
    console.log('📡 [emitCacheUpdated] Broadcast via BroadcastChannel');
    broadcastCacheUpdate(topicStr);
  }).catch(() => {
    console.log('📡 [emitCacheUpdated] Fallback DOM Event');
    // Fallback si broadcast pas disponible
    window.dispatchEvent(new CustomEvent('cc:cache-updated', { detail: { topic: topicStr, at: Date.now() } }));
  });
  
  // Toujours émettre l'événement DOM aussi
  console.log('📡 [emitCacheUpdated] Émission DOM Event cc:cache-updated');
  window.dispatchEvent(new CustomEvent('cc:cache-updated', { detail: { topic: topicStr, at: Date.now() } }));
}

/**
 * Migre automatiquement l'ancien cache vers la nouvelle version
 * Supprime toutes les clés avec une ancienne version
 * À appeler au démarrage de l'application
 */
export function migrateCache() {
  try {
    const oldKeys = [];
    const migrationKey = `${NS}:cache-migration-v${CACHE_VERSION}`;
    
    // Vérifier si migration déjà effectuée
    if (localStorage.getItem(migrationKey)) {
      return;
    }
    
    // Trouver toutes les anciennes clés
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(`${NS}:`)) {
        // Si la clé ne contient pas la version actuelle, c'est une ancienne clé
        if (!key.includes(`:v${CACHE_VERSION}:`)) {
          oldKeys.push(key);
        }
      }
    }
    
    if (oldKeys.length > 0) {
      console.log(`🔄 [migrateCache] Migrating ${oldKeys.length} old cache entries to v${CACHE_VERSION}...`);
      oldKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.warn('[migrateCache] Failed to remove key:', key, e);
        }
      });
      console.log(`✅ [migrateCache] Migration complete`);
    }
    
    // Marquer la migration comme effectuée
    localStorage.setItem(migrationKey, JSON.stringify({ 
      version: CACHE_VERSION, 
      migratedAt: Date.now() 
    }));
  } catch (e) {
    console.error('[migrateCache] Migration failed:', e);
  }
}